import { formValues as emptyBillingFormValues } from 'components/billingForm/hooks';
import { BillingFormValues } from 'components/billingForm/types';
import { FormValues as CandidateDatabaseFormValues } from 'components/candidateDatabasePackageOrderForm/CandidateDatabasePackageOrderForm';
import { FormValues as HighlightedPackagesFormValues } from 'components/highlightedPackageOrderForm/HighlightedPackageOrderForm';
import { FormValues as AdPackageFormValues } from 'components/packageOrderForm/packageOrderFormTypes';
import { FormValues as SocialPostingFormValues } from 'components/socialPostingPackageOrderForm/SocialPostingPackageOrderForm';
import { FormValues as TopResultPackagesFormValues } from 'components/topResultPackageOrderForm/TopResultPackageOrderForm';
import { Map } from 'immutable';
import actions from './actions';

export type OrderState = {
  jobPackages: AdPackageFormValues | undefined;
  candidatePackages: CandidateDatabaseFormValues | undefined;
  socialPostingPackages: SocialPostingFormValues | undefined;
  highlightedPackages: HighlightedPackagesFormValues | undefined;
  topResultPackages: TopResultPackagesFormValues | undefined;
};

export type OrderWithBillingState = {
  order: OrderState;
  paymentDetails: BillingFormValues;
  upsaleVisible: boolean;
};

const initState = Map<keyof OrderWithBillingState, OrderState | BillingFormValues | boolean>({
  order: {
    jobPackages: undefined,
    candidatePackages: undefined,
    socialPostingPackages: undefined,
    highlightedPackages: undefined,
    topResultPackages: undefined,
  },
  paymentDetails: emptyBillingFormValues,
  upsaleVisible: false,
});

export type PackagesOrderAction =
  | {
      type: typeof actions.ADD_JOB_PACKAGE;
      payload: OrderState['jobPackages'];
    }
  | {
      type: typeof actions.ADD_CANDIDATE_SEARCH_PACKAGE;
      payload: OrderState['candidatePackages'];
    }
  | {
      type: typeof actions.ADD_SOCIAL_POSTING_PACKAGE;
      payload: OrderState['socialPostingPackages'];
    }
  | {
      type: typeof actions.ADD_HIGHLIGHTED_PACKAGE;
      payload: OrderState['highlightedPackages'];
    }
  | {
      type: typeof actions.ADD_TOP_RESULT_PACKAGE;
      payload: OrderState['topResultPackages'];
    }
  | {
      type: typeof actions.REMOVE_PACKAGE;
      payload: keyof OrderState;
    }
  | {
      type: typeof actions.UPDATE_PAYMENT_DETAILS;
      payload: OrderWithBillingState['paymentDetails'];
    }
  | {
      type: typeof actions.RESET_ORDER;
    }
  | {
      type: typeof actions.TOGGLE_UPSALE_VISIBLE;
      payload: boolean;
    };

export default function packagesSubmissionReducer(state = initState, action: PackagesOrderAction) {
  switch (action.type) {
    case actions.ADD_JOB_PACKAGE:
      return state.set('order', {
        ...(state.get('order') as OrderState),
        jobPackages: action.payload,
      });
    case actions.ADD_CANDIDATE_SEARCH_PACKAGE:
      return state.set('order', {
        ...(state.get('order') as OrderState),
        candidatePackages: action.payload,
      });
    case actions.ADD_SOCIAL_POSTING_PACKAGE:
      return state.set('order', {
        ...(state.get('order') as OrderState),
        socialPostingPackages: action.payload,
      });
    case actions.ADD_HIGHLIGHTED_PACKAGE:
      return state.set('order', {
        ...(state.get('order') as OrderState),
        highlightedPackages: action.payload,
      });
    case actions.ADD_TOP_RESULT_PACKAGE:
      return state.set('order', {
        ...(state.get('order') as OrderState),
        topResultPackages: action.payload,
      });
    case actions.REMOVE_PACKAGE:
      return state.set('order', {
        ...(state.get('order') as OrderState),
        [action.payload]: undefined,
      });
    case actions.UPDATE_PAYMENT_DETAILS:
      return state.set('paymentDetails', action.payload);
    case actions.RESET_ORDER:
      return state.set('order', initState.get('order'));
    case actions.TOGGLE_UPSALE_VISIBLE:
      return state.set('upsaleVisible', action.payload);
    default:
      return state;
  }
}
